import { IPageConfig, IPortalUserConfig, ISavedPreset } from 'api/portalUser/types';
import { IAllPageFilters, INullable } from 'utils/types';

import { IComposeFiltersProps, IUpdateSearchProps } from './types';

const DEFAULT_NAVIGATION = { page: 0, size: 10, sort: {} };

export const RECENT_FILTER_NAME = 'common.filters.recentFilters';

export const removeHiddenColumnsFromSearch = (
  search: IAllPageFilters['search'],
  hiddenColumns: IComposeFiltersProps['hiddenColumns'],
): IAllPageFilters['search'] => {
  if (!hiddenColumns || !search) {
    return search;
  }

  const filteredSearchArray = Object.entries(search).filter(
    ([searchColumnKey]) => !hiddenColumns[searchColumnKey],
  );

  return Object.fromEntries(filteredSearchArray);
};

export const composeInitialFilters = ({
  disableUrlFilters,
  hiddenColumns,
  initialFilters,
  presetFilters,
  recordsPerPage,
  urlFilters,
  urlSearchQuery,
}: IComposeFiltersProps): IAllPageFilters => {
  const initialSearch = initialFilters?.search || {};

  if (urlSearchQuery) {
    return {
      ...DEFAULT_NAVIGATION,
      search: removeHiddenColumnsFromSearch(
        { ...initialSearch, ...urlSearchQuery },
        hiddenColumns,
      ),
      size: recordsPerPage,
    };
  }

  if (urlFilters && !disableUrlFilters) {
    /*
      if urlFilters.search is present then it means this is shared filters and presetFilters should be excluded.
      But if urlFilters.search is not present it means only pages, size or sort applied on current preset and we should include presetFilters
    */
    const presetFiltersSearch = urlFilters.search ? {} : presetFilters;
    return {
      ...DEFAULT_NAVIGATION,
      ...urlFilters,
      search: removeHiddenColumnsFromSearch(
        {
          ...urlFilters.search,
          ...initialSearch,
          ...presetFiltersSearch,
        } as IAllPageFilters['search'],
        hiddenColumns,
      ),
      size: recordsPerPage,
    };
  }

  if (presetFilters && Object.keys(presetFilters).length) {
    return {
      ...DEFAULT_NAVIGATION,
      ...initialFilters,
      search: removeHiddenColumnsFromSearch(
        { ...initialSearch, ...presetFilters } as IAllPageFilters['search'],
        hiddenColumns,
      ),
      size: recordsPerPage,
    };
  }

  return {
    ...DEFAULT_NAVIGATION,
    size: recordsPerPage,
    ...initialFilters,
    search: removeHiddenColumnsFromSearch({ ...initialSearch }, hiddenColumns),
  };
};

export const handleSearchFilterState = ({ search, searchProps }: IUpdateSearchProps) => {
  const { columnKey, ...restSearch } = searchProps;

  if (!restSearch.value) {
    const key = columnKey as keyof typeof search;
    const { [key]: _, ...restState } = search;
    return restState;
  }

  return {
    ...search,
    [columnKey]: restSearch,
  };
};

export interface IGetPresetsResult {
  activeFilterPreset?: ISavedPreset;
  activeFilterPresetId: INullable<string>;
  filterPresets: ISavedPreset[];
}

interface IGetPresetsProps {
  pageKey?: string;
  portalUser?: IPortalUserConfig;
}

export const getPresets = ({
  pageKey,
  portalUser,
}: IGetPresetsProps): IGetPresetsResult => {
  const { settings, systemSettings } = portalUser || {};

  const userPage = pageKey ? settings?.pages?.[pageKey] : ({} as IPageConfig);
  const systemPage = pageKey ? systemSettings?.pages?.[pageKey] : ({} as IPageConfig);

  const filterPresets = [...(systemPage?.presets || []), ...(userPage?.presets || [])];

  const activeFilterPresetId = userPage?.activePreset || null;

  const activeFilterPreset = filterPresets.find(
    (preset) => preset.id === userPage?.activePreset,
  );

  return {
    activeFilterPreset,
    activeFilterPresetId,
    filterPresets,
  };
};
